/**
 * The right-hand bar from the Project Landing Page wireframe, made part of the layout so that it is
 * on every page rather than belonging to one.
 *
 * Collapsible to a narrow rail, and resizable by dragging its left edge (or, focused, with the arrow
 * keys; double-click puts it back to 320). Both are remembered per browser, because they are reading
 * preferences rather than state anyone else needs. The headed lists of the Synthesis V3 spec scroll at
 * the top; the Ask box, FACE and Time Machine stay at the foot.
 *
 * Hidden below `md`: at phone widths there is no room beside the content, and FACE needs a home
 * there that has not been designed yet.
 */

import type { KeyboardEvent, PointerEvent } from 'react';
import { useRef, useState } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { Theme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { AskBox, BarLists, TimeMachine } from './BarLists';
import FacePanel from './FacePanel';

const DEFAULT_WIDTH = 320;
const MIN_WIDTH = 260;
const MAX_WIDTH = 560;
const KEY_STEP = 16;
const CLOSED_WIDTH = 52;
const STORAGE_KEY = 'ntab.rightBar.open';
const WIDTH_KEY = 'ntab.rightBar.width';

const clamp = (w: number) => Math.round(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w)));

// Storage can throw (private windows, blocked site data); the bar works the same, just unremembered.
function read(key: string) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not remembered, nothing else lost.
  }
}

const readOpen = () => read(STORAGE_KEY) !== 'false';
const readWidth = () => {
  const stored = Number(read(WIDTH_KEY));
  return stored ? clamp(stored) : DEFAULT_WIDTH;
};

export default function RightBar({ top }: { top: number }) {
  const [open, setOpen] = useState(readOpen);
  const [width, setWidth] = useState(readWidth);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; width: number } | null>(null);

  const toggle = () => {
    setOpen((was) => {
      write(STORAGE_KEY, String(!was));
      return !was;
    });
  };

  const resize = (next: number) => {
    const w = clamp(next);
    setWidth(w);
    write(WIDTH_KEY, String(w));
  };

  // The handle captures the pointer, so the drag keeps tracking when the cursor leaves the 8px strip
  // or crosses the page content. The bar grows leftward: moving left widens it.
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, width };
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    setWidth(clamp(drag.current.width + drag.current.x - e.clientX));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    resize(drag.current.width + drag.current.x - e.clientX);
    drag.current = null;
    setDragging(false);
  };

  const onHandleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const next = { ArrowLeft: width + KEY_STEP, ArrowRight: width - KEY_STEP, Home: MIN_WIDTH, End: MAX_WIDTH }[e.key];
    if (next === undefined) return;
    e.preventDefault();
    resize(next);
  };

  const label = open ? 'Collapse side panel' : 'Expand side panel';

  return (
    <Box
      component="aside"
      aria-label="Side panel"
      sx={(theme: Theme) => ({
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        flex: 'none',
        width: open ? width : CLOSED_WIDTH,
        // No easing while dragging, or the edge lags behind the pointer.
        transition: dragging ? 'none' : theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
        userSelect: dragging ? 'none' : undefined,
        position: 'sticky',
        top,
        alignSelf: 'flex-start',
        height: `calc(100vh - ${top + 20}px)`,
        mt: `${top}px`,
        mr: '20px',
        borderRadius: 2,
        bgcolor: 'grey.100',
        overflow: 'hidden',
        ...theme.applyStyles('dark', { bgcolor: 'dark.800' })
      })}
    >
      {open && (
        <Box
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize side panel"
          aria-controls="right-bar-body"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          aria-valuenow={width}
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={() => resize(DEFAULT_WIDTH)}
          onKeyDown={onHandleKey}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 8,
            zIndex: 3,
            cursor: 'col-resize',
            touchAction: 'none',
            outline: 'none',
            // A thin line on hover, focus and drag, so the edge announces itself without a permanent rule.
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 12,
              bottom: 12,
              left: 2,
              width: 3,
              borderRadius: 2,
              bgcolor: 'primary.main',
              opacity: dragging ? 1 : 0,
              transition: 'opacity 120ms ease'
            },
            '&:hover::after, &:focus-visible::after': { opacity: 1 }
          }}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: open ? 'flex-start' : 'center', p: 1 }}>
        <Tooltip title={label} placement="left">
          <IconButton size="small" onClick={toggle} aria-label={label} aria-expanded={open} aria-controls="right-bar-body">
            {open ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Hidden with display:none rather than unmounted, so collapsing keeps the FACE selection
          and a closed bar still has nothing in the tab order. */}
      <Box id="right-bar-body" sx={{ flex: 1, minHeight: 0, display: open ? 'flex' : 'none', flexDirection: 'column', px: 2, pb: 2 }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mx: -0.5, px: 0.5 }}>
          <BarLists />
        </Box>
        {/* 24px under the Ask box: FACE's drop shadow eats into a smaller gap, and it should read as its own block. */}
        <Box sx={{ pt: 2, pb: 3 }}>
          <AskBox />
        </Box>
        <FacePanel />
        <Box sx={{ pt: 2.5 }}>
          <TimeMachine />
        </Box>
      </Box>
    </Box>
  );
}
